import { Link } from 'react-router';
import type { Leverandor } from '../api/types';
import { VURDERINGER_AKTIVERT } from '../lib/leverandorvurderinger';

/** Stjernene, som tall og som noe en skjermleser kan lese. */
export function Stjerner({ snitt, antall }: { snitt: number | null; antall: number }) {
  if (snitt === null || antall === 0) {
    return <span className="stjerner tom">Ingen vurderinger ennå</span>;
  }
  const hele = Math.round(snitt);
  return (
    <span className="stjerner" aria-label={`${snitt} av 5 stjerner, ${antall} vurderinger`}>
      <span className="stjernerad" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((n) => (
          <span key={n} className={n <= hele ? 'stjerne fylt' : 'stjerne'}>
            ★
          </span>
        ))}
      </span>
      <span className="stjernetall">
        {snitt.toFixed(1)} <span className="antall">({antall})</span>
      </span>
    </span>
  );
}

export function prisTekst(prisFra: number | null): string | null {
  if (prisFra === null) return null;
  return `Fra ${prisFra.toLocaleString('nb-NO')} kr`;
}

/**
 * Kortet som vises både i lista og oppå kartet.
 *
 * Ett kort, to steder. Det er med vilje: den som har sett kortet i kartet skal
 * kjenne igjen det samme kortet i lista, og et lokale skal ikke se ut som to
 * forskjellige tilbud avhengig av hvor man kom fra.
 *
 * Kategorien står ikke på kortet. Registeret viser bare selskapslokaler, og en
 * etikett som sier det samme på hvert eneste kort, sier ingenting – se
 * `LeverandorerPage`.
 *
 * Stjernene står bare når vurderingene er på – se `lib/leverandorvurderinger`.
 * Det gjelder også «Ingen vurderinger ennå»: det er en oppfordring om noe man
 * ikke kan gjøre.
 *
 * Bildet står bare når det finnes et bilde. Lokalene har ikke sendt oss noen
 * ennå, og et grått felt der bildet skulle vært, sier bare at noe mangler.
 * Uten bilde er kortet teksten alene; med bilde er det kortet slik det alltid
 * har vært, så den dagen bildene kommer, er det ingenting å bygge.
 */
export function Leverandorkort({
  leverandor,
  kompakt = false,
  lenke = true,
  onLukk,
}: {
  leverandor: Leverandor;
  kompakt?: boolean;
  /**
   * Skal navnet være en lenke til annonsesida?
   *
   * Av i lokalets egen forhåndsvisning. Der finnes ikke sida å gå til – den
   * som ser kortet er ikke logget inn – og en lenke som ikke fører noe sted,
   * er verre enn ingen lenke.
   */
  lenke?: boolean;
  onLukk?: () => void;
}) {
  const pris = prisTekst(leverandor.prisFra);
  return (
    <article className={`leverandorkort${kompakt ? ' kompakt' : ''}`}>
      {onLukk ? (
        <button type="button" className="kortlukk" onClick={onLukk} aria-label="Lukk kortet">
          ×
        </button>
      ) : null}

      {leverandor.forsidebilde ? (
        <img
          className="kortbilde"
          src={`/api/leverandorbilder/${leverandor.forsidebilde}`}
          alt=""
          loading="lazy"
        />
      ) : null}

      <div className="kortinnhold">
        <h3>
          {lenke ? (
            <Link to={`/app/leverandorer/${leverandor.id}`}>{leverandor.navn}</Link>
          ) : (
            leverandor.navn
          )}
        </h3>
        {leverandor.poststed ? <p className="kortsted">{leverandor.poststed}</p> : null}
        {leverandor.kortBeskrivelse ? (
          <p className="kortbeskrivelse">{leverandor.kortBeskrivelse}</p>
        ) : null}
        {VURDERINGER_AKTIVERT || pris ? (
          <div className="kortbunn">
            {VURDERINGER_AKTIVERT ? (
              <Stjerner snitt={leverandor.snitt} antall={leverandor.antallVurderinger} />
            ) : null}
            {pris ? <span className="kortpris">{pris}</span> : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}
